import React from 'react';
import {
  getDocs,
  collection,
  Firestore,
  query,
  QueryConstraint,
  where,
  limit,
  orderBy,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  startAfter,
  endBefore,
  getCount,
  limitToLast
} from 'firebase/firestore/lite';
import { useFirestoreClient } from 'src/firebase';
import { getFieldName } from 'src/models/messageRoom';
import { Collection } from 'src/models/common';
import {
  useLoginAccount,
  LoginAccountState,
} from 'src/globalStates/loginAccount';
import { MessageRoomFilterReturnData, MessageRoomRepository, MessageRoomReturnData } from './interface';
import { toMessageRoomFromDoc } from './converter';
import {
  MessageRoomGetItemQuery,
  MessageRoomListQuery,
} from 'src/usecases/messageRoom/reader';
import { validData } from 'src/util/validate';
import { toUpdateFsDoc } from '../common/firestore';
import { message } from 'antd';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { PAGE_SIZE } from 'src/components/page/directMessage/directMessage';

// 最大フィルター結果数
const MAX_FILTER_RESULTS = 50;

export const useMessageRoomRepository = (): MessageRoomRepository => {
  const db = useFirestoreClient();
  const account = useLoginAccount();

  return React.useMemo(
    () => createMessageRoomRepository(db, account),
    [db, account]
  );
};

const createMessageRoomRepository = (
  db: Firestore,
  account: LoginAccountState
) => {
  return {
    async getList({ firstDoc, lastDoc }: MessageRoomListQuery) {
      if (account.merchantId === undefined) {
        throw new Error('エラーが発生しました');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId!.toString()}/${
          Collection.MessageRooms
        }`
      );

      const queries: Array<QueryConstraint> = [
        where(getFieldName('documentStatus'), '==', 'active'),
        orderBy(getFieldName('lastMessageAt'), 'desc'),
      ];

      if (!account.master) {
        queries.push(where(getFieldName('storeId'), 'in', account.storeIds));
      }

      // クエリのリミット設定、デフォルトはlimit使用
      let qLimit = limit(PAGE_SIZE);

      // ページング処理
      if (firstDoc !== undefined) {
        // 前のページ分のデータを取得
        queries.push(endBefore(firstDoc));
        qLimit = limitToLast(PAGE_SIZE);
      }
      else if (lastDoc !== undefined) {
        // 次のページ分のデータを取得
        queries.push(startAfter(lastDoc));
      }

      // メッセージルームの取得
      const snapshot = await getDocs(query(collRef, ...queries, qLimit));

      return {
        messageRooms: snapshot.docs.map((doc) => toMessageRoomFromDoc(doc.data(), doc.id)),
        firstDoc: snapshot.docs.length > 0 ? snapshot.docs[0] as QueryDocumentSnapshot : undefined,
        lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot : undefined,
      };
    },

    async getDocFromMCusId({ mCustomerId }: MessageRoomListQuery) {
      if (account.merchantId === undefined || mCustomerId === undefined) {
        throw new Error('エラーが発生しました');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageRooms
        }`
      );
      const queries: Array<QueryConstraint> = [
        where(getFieldName('documentStatus'), '==', 'active'),
        where(getFieldName('mCusId'), '==', mCustomerId),
      ];

      // メッセージルームの取得
      const snapshot = await getDocs(query(collRef, ...queries, limit(PAGE_SIZE)));

      return {
        messageRooms: snapshot.docs.map((doc) => toMessageRoomFromDoc(doc.data(), doc.id)),
        firstDoc: snapshot.docs.length > 0 ? snapshot.docs[0] as QueryDocumentSnapshot : undefined,
        lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot : undefined,
      }
    },

    async getDoc({ mRoomId }: MessageRoomGetItemQuery) {
      if (!!!account.merchantId || !!!mRoomId) {
        throw new Error('エラーが発生しました');
      }

      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageRooms
        }/${mRoomId.toString()}`
      );

      const snapshot = await getDoc(docRef);

      if (!snapshot.exists) {
        return null;
      }

      const _mRoom = toMessageRoomFromDoc(snapshot.data()!, snapshot.id);
      if (!account.storeIds?.includes(_mRoom.storeId)) {
        return null;
      }

      if (!validData([_mRoom.storeId], account)) {
        throw new Error('権限がありません');
      }

      return _mRoom;
    },

    async update({ mRoomId }: MessageRoomGetItemQuery) {
      if (!!!account.merchantId || !!!mRoomId) {
        throw new Error('エラーが発生しました');
      }

      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageRooms
        }/${mRoomId.toString()}`
      );

      // アップデートデータを前処理
      const docSnap = await getDoc(docRef);
      let updateData:any = toUpdateFsDoc({ unreadMessages: 0,});

      if (docSnap.exists()) {
        const getData = docSnap.data();
        if (getData.hasOwnProperty('isImportant')) {
          await updateDoc(docRef, updateData);
        }
        else {
          await updateDoc(docRef, { isImportant: false, ...updateData });
        }
      }
    },

    // 重要情報を設定するupdate関数
    async updateIsImportant(mRoomId: string , isImportant: boolean): Promise<boolean> {
      
      // オフライン状態を確認
      if (!navigator.onLine) {
        message.error('オフライン状態です。インターネット接続を確認してください。');
        return true;
      }

      if (!!!account.merchantId || !!!mRoomId) {
        throw new Error('エラーが発生しました。');
      }

      const docRef = doc(
        db,
        `${Collection.Merchants}/${account.merchantId!.toString()}/${Collection.MessageRooms
        }/${mRoomId}`
      );

      // 返り値設定
      let ret = true;
      try {
        const dt = serverTimestamp();
        await updateDoc(docRef, { isImportant: isImportant, updatedAt: dt });
        message.success('重要度を反映しました。');
      } catch (err) {
        ret = false;
        throw new Error('エラーが発生しました。');
      }
      finally {
        return ret;
      }
    },

    // 重要設定(true)がされているメッセージルームを取得する
    async getDocIsImportant( { firstDoc, lastDoc }: MessageRoomListQuery) {
      // マーチャントIDが取得できない場合
      if (account.merchantId === undefined) {
        throw new Error('エラーが発生しました');
      }

      // メッセージルームのコレクションを取得
      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageRooms
        }`
      );

      // クエリの設定
      const queries: Array<QueryConstraint> = [
        where(getFieldName('documentStatus'), '==', 'active'),
        where(getFieldName('isImportant'), '==', true),
        orderBy(getFieldName('lastMessageAt'), 'desc'),
      ];

      // リミット設定
      let qLimit = limit(PAGE_SIZE);
      // ページング処理
      if (firstDoc !== undefined) {
        // 前のページ分のデータを取得
        queries.push(endBefore(firstDoc));
        qLimit = limitToLast(PAGE_SIZE);
      }
      else if (lastDoc !== undefined) {
        // 次のページ分のデータを取得
        queries.push(startAfter(lastDoc));
      }

      // メッセージルームの取得
      const snapshot = await getDocs(query(collRef, ...queries, qLimit));

      return {
        messageRooms: snapshot.docs.map((doc) => toMessageRoomFromDoc(doc.data(), doc.id)),
        firstDoc: snapshot.docs.length > 0 ? snapshot.docs[0] as QueryDocumentSnapshot : undefined,
        lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot : undefined,
      };
    },

    // リストの総件数を取得する
    async getCount({ mCustomerId, searchFilters, isImportant }: MessageRoomListQuery) {
      // マーチャントIDが取得できない場合
      if (account.merchantId === undefined) {
        throw new Error('エラーが発生しました');
      }

      // メッセージルームのコレクションを取得
      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId!.toString()}/${
          Collection.MessageRooms
        }`
      );

      // 検索フィルターがある場合
      if (searchFilters && searchFilters.length > 0) {
        if (searchFilters.length === 1) {
          // 単一フィルター検索
          const filter = searchFilters[0];
          const queries: Array<QueryConstraint> = [
            where(getFieldName('documentStatus'), '==', 'active'),
          ];

          // フィルターのフィールドに応じてクエリを設定
          if (filter.field === 'mCusId') {
            queries.push(where(getFieldName('mCusId'), '==', filter.value));
          }
          else if (filter.field === 'nickname') {
            queries.push(where(getFieldName('nickname'), '==', filter.value));
          }
          else if (filter.field === 'mCusName') {
            queries.push(where(getFieldName('mCusName'), '==', filter.value));
          }

          if (!account.master) {
            queries.push(where(getFieldName('storeId'), 'in', account.storeIds));
          }

          // 重要度フィルターがある場合：trueの場合のみクエリに追加
          if (isImportant === true) {
            queries.push(where(getFieldName('isImportant'), '==', true));
          }

          const snapshot = await getCount(query(collRef, ...queries));
          return snapshot.data().count;
        } else {
          // 複数フィルター検索の場合は0を返す
          return 0;
        }
      }

      if (mCustomerId !== null && mCustomerId !== '') {
        // mCusId検索を行う場合
        const queries: Array<QueryConstraint> = [
          where(getFieldName('documentStatus'), '==', 'active'),
          where(getFieldName('mCusId'), '==', mCustomerId),
        ];

        const snapshot = await getCount(query(collRef, ...queries));
        return snapshot.data().count;
      }
      else if (isImportant !== undefined && isImportant === true) {
        // 重要度絞り込み設定している場合
        const queries: Array<QueryConstraint> = [
          where(getFieldName('documentStatus'), '==', 'active'),
          where(getFieldName('isImportant'), '==', isImportant),
        ];

        const snapshot = await getCount(query(collRef, ...queries));
        return snapshot.data().count;
      }
      else {
        // それ以外
        const queries: Array<QueryConstraint> = [
          where(getFieldName('documentStatus'), '==', 'active'),
        ];

        if (!account.master) {
          queries.push(where(getFieldName('storeId'), 'in', account.storeIds));
        }

        const snapshot = await getCount(query(collRef, ...queries));
        return snapshot.data().count;
      }
    },

    // 単一フィルター検索（ページング有効）
    async searchBySingleFilter({ searchFilters, firstDoc, lastDoc, isImportant }: MessageRoomListQuery): Promise<MessageRoomReturnData> {
      if (account.merchantId === undefined || !searchFilters || searchFilters.length !== 1) {
        throw new Error('エラーが発生しました');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageRooms
        }`
      );

      const filter = searchFilters[0]; // 単一フィルターを取得
      const queries: Array<QueryConstraint> = [
        where(getFieldName('documentStatus'), '==', 'active'),
      ];

      // フィルターのフィールドに応じてクエリを設定
      if (filter.field === 'mCusId') {
        queries.push(where(getFieldName('mCusId'), '==', filter.value));
      }
      else if (filter.field === 'nickname') {
        queries.push(where(getFieldName('nickname'), '==', filter.value));
      }
      else if (filter.field === 'mCusName') {
        queries.push(where(getFieldName('mCusName'), '==', filter.value));
      }

      // lastMessageAtでソート
      queries.push(orderBy(getFieldName('lastMessageAt'), 'desc'));

      if (!account.master) {
        queries.push(where(getFieldName('storeId'), 'in', account.storeIds));
      }

      // 重要度フィルターがある場合：trueの場合のみクエリに追加
      if (isImportant === true) {
        console.log('isImportant:', isImportant);
        queries.push(where(getFieldName('isImportant'), '==', isImportant));
      }

      // ページング処理（通常のFirestoreページング）
      let qLimit = limit(PAGE_SIZE);
      if (firstDoc !== undefined) {
        queries.push(endBefore(firstDoc));
        qLimit = limitToLast(PAGE_SIZE);
      } else if (lastDoc !== undefined) {
        queries.push(startAfter(lastDoc));
      }

      const snapshot = await getDocs(query(collRef, ...queries, qLimit));

      return {
        messageRooms: snapshot.docs.map((doc) => toMessageRoomFromDoc(doc.data(), doc.id)),
        firstDoc: snapshot.docs.length > 0 ? snapshot.docs[0] as QueryDocumentSnapshot : undefined,
        lastDoc: snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] as QueryDocumentSnapshot : undefined,
      };
    },

    // 複数フィルター検索（OR検索、ページング無効）
    async searchByMultipleFilters({ searchFilters, isImportant }: MessageRoomListQuery): Promise<MessageRoomFilterReturnData> {
      if (account.merchantId === undefined || !searchFilters || searchFilters.length < 2) {
        throw new Error('エラーが発生しました');
      }

      const collRef = collection(
        db,
        `${Collection.Merchants}/${account.merchantId.toString()}/${
          Collection.MessageRooms
        }`
      );

      const allResults: QueryDocumentSnapshot[] = [];
      let hasWarning = false;
      let hasError = false;
      const errorFilters: string[] = [];

      // 各フィルター条件で順次検索（各MAX_FILTER_RESULTS件まで）
      for (const filter of searchFilters) {
        try {
          const queries: Array<QueryConstraint> = [
            where(getFieldName('documentStatus'), '==', 'active'),
          ];

          // フィルターのフィールドに応じてクエリを設定
          if (filter.field === 'mCusId') {
            queries.push(where(getFieldName('mCusId'), '==', filter.value));
          }
          else if (filter.field === 'nickname') {
            queries.push(where(getFieldName('nickname'), '==', filter.value));
          }
          else if (filter.field === 'mCusName') {
            queries.push(where(getFieldName('mCusName'), '==', filter.value));
          }

          // lastMessageAtでソート
          queries.push(orderBy(getFieldName('lastMessageAt'), 'desc'));

          if (!account.master) {
            queries.push(where(getFieldName('storeId'), 'in', account.storeIds));
          }

          // 重要度フィルターがある場合：trueの場合のみクエリに追加
          if (isImportant === true) {
            queries.push(where(getFieldName('isImportant'), '==', isImportant));
          }

          // 各フィルターで最大MAX_FILTER_RESULTS件取得
          const snapshot = await getDocs(query(collRef, ...queries, limit(MAX_FILTER_RESULTS)));
          allResults.push(...(snapshot.docs as QueryDocumentSnapshot[]));

          // MAX_FILTER_RESULTS件ピッタリの場合は警告フラグを設定
          if (snapshot.docs.length === MAX_FILTER_RESULTS) {
            hasWarning = true;
          }

          // PAGE_SIZE件に達したら集計終了
          if (allResults.length >= PAGE_SIZE) {
            hasWarning = true; // PAGE_SIZE件で打ち切った場合も警告
            break;
          }
        } catch (error) {
          // 個別フィルターでエラーが発生した場合、ユーザー向けの情報を記録して継続
          hasError = true;
          errorFilters.push(`${filter.label}: ${filter.value}`);
          // 次のフィルターで検索を継続
          continue;
        }
      }

      // 全てのフィルターでエラーが発生した場合のみ例外をスロー
      if (hasError && allResults.length === 0) {
        throw new Error('検索処理中にエラーが発生しました。時間をおいて再度お試しください。');
      }

      // 重複除去（messageRoomIdベース）
      const uniqueResults = allResults.filter((doc, index, self) =>
        self.findIndex(d => d.id === doc.id) === index
      );

      // lastMessageAtでソート（念のため再ソート）
      uniqueResults.sort((a, b) => {
        const aTime = a.data().lastMessageAt?.toMillis() || 0;
        const bTime = b.data().lastMessageAt?.toMillis() || 0;
        return bTime - aTime; // 降順
      });

      // 最大PAGE_SIZE件に制限
      const finalResults = uniqueResults.slice(0, PAGE_SIZE);

      // 警告メッセージ（エラー情報も含む）
      let warning: string | undefined;
      if (hasWarning || uniqueResults.length >= PAGE_SIZE) {
        warning = `検索結果が多いため、新しいメッセージ順で${PAGE_SIZE}件まで表示しています。より詳細な検索には条件を絞り込んでください。`;
      }
      
      // エラーがあった場合は警告メッセージに追記
      if (hasError && errorFilters.length > 0) {
        const errorMessage = `一部の検索条件でエラーが発生しました: ${errorFilters.join(', ')}`;
        warning = warning ? `${warning}\n${errorMessage}` : errorMessage;
      }

      return {
        messageRooms: finalResults.map((doc) => toMessageRoomFromDoc(doc.data(), doc.id)),
        warning: warning,
      };
    }
  };
};
